import React, { useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';

import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import ShipmentDetailsSectionContent from './content';
import { ExchangeIndicator } from './exchange-indicator/exchange-indicator';
import { GiftIndicator } from './gift-indicator/gift-indicator';
import messages from './messages';
import style from './shipment-details.module.less';
import { PremiumProgramTags } from '../../../../../components/premium-programs/premium-program-tags';
import { emptyArray } from 'ui-components/default-props/empty';
import { DemoTooltipStar } from '../../../../../demos/components/demo-tooltip-star/demo-tooltip-star';
import { useContainsDemoOrder } from '../../../../../demos/hooks/use-contains-demo-order';
import { useEnablePremiumProgramTags } from '../../../../../feature-flags/hooks/use-enable-premium-program-tags';
import { useFulfillmentPlanIdContext } from '../../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import Global from '../../../../../store/global-selectors';
import ExpanderSection from '../../../tab-section/expander-section/expander-section';
import SectionTitle from '../../../tab-section/title';
import { OrderDetailsSectionId, useOrderDetailsExpanderSection } from '../../order-details-expanded-section-provider';

const sectionId = OrderDetailsSectionId.ShipmentDetails;

/**
 * @param {object} props
 * @param {number} props.counter - Current shipment counter
 * @param {number} props.total - Total number of shipments
 * @returns {JSX.Element}
 */
export const ShipmentDetails = ({ counter, total }) => {
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const fp = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const { isGift, premiumPrograms, isExchange } = fp || {};
  const { isExpanded } = useOrderDetailsExpanderSection(sectionId);
  const isPremiumProgramTagsEnabled = useEnablePremiumProgramTags();
  const isDemoOrder = useContainsDemoOrder([fulfillmentPlanId]);

  const showGiftIndicator = isGift && !isExpanded;

  const shipmentCountFormattedMessageValues = useMemo(() => {
    return {
      count: <FormattedNumber value={counter} />,
      total: <FormattedNumber value={total} />,
    };
  }, [counter, total]);

  return (
    <ExpanderSection
      ariaLabelMessageDescriptor={messages.sectionAriaLabel}
      contentClassName={style['shipment-details-section-content']}
      headerContent={
        <FlexRow className={style['shipment-details-section-header']}>
          <FlexRow className={style.title}>
            <SectionTitle messageDescriptor={messages.sectionTitle} />
            {isDemoOrder ? <DemoTooltipStar altText={messages.demoTooltipAltText} tooltipText={messages.demoTooltipText} /> : null}
            {isPremiumProgramTagsEnabled ? <PremiumProgramTags premiumPrograms={premiumPrograms || emptyArray} /> : null}
            {isExchange ? <ExchangeIndicator /> : null}
            {showGiftIndicator ? <GiftIndicator /> : null}
          </FlexRow>
          {total > 1 && (
            <Text
              className={style['shipment-count']}
              formattedMessageValues={shipmentCountFormattedMessageValues}
              textType={TextType.Caption}
              value={messages.shipmentCounter}
            />
          )}
        </FlexRow>
      }
      sectionContent={<ShipmentDetailsSectionContent />}
      sectionId={sectionId}
    />
  );
};
